import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Button, Modal } from 'react-bootstrap';

const PLACEHOLDER =
  'https://via.placeholder.com/800x600/f2f2f2/999999?text=Equipment+Image';

const VIDEO_EXTENSIONS = /\.(mp4|mov|webm|ogg|avi|mkv|m4v)(\?|$)/i;

function isVideoUrl(url) {
  return url && typeof url === 'string' && VIDEO_EXTENSIONS.test(url);
}

function buildImageList(detail) {
  if (!detail) return [];
  const cover = detail.photoUrl || '';
  const raw = Array.isArray(detail.photoArray) ? detail.photoArray : [];
  const urls = [];
  if (cover) urls.push(cover);
  raw.forEach((u) => {
    if (u && typeof u === 'string' && u.trim() && !urls.includes(u.trim())) {
      urls.push(u.trim());
    }
  });
  return urls.length ? urls : [PLACEHOLDER];
}

function Chip({ variant = 'yellow', children }) {
  const styles = {
    yellow: {
      background: '#fff6d6',
      color: '#7a5b00',
      border: '1px solid #ffe08a',
    },
    green: {
      background: '#f0fdf4',
      color: '#166534',
      border: '1px solid #bbf7d0',
    },
  };
  const style = styles[variant] || styles.yellow;
  return (
    <span
      style={{
        ...style,
        borderRadius: '999px',
        padding: '2px 10px',
        fontSize: '12px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/**
 * Full-screen style modal: carousel (cover first, then photoArray) + equipment metadata & details.
 */
export default function EquipmentInfoGalleryModal({
  open,
  onClose,
  detail,
  initialImageIndex = 0,
}) {
  const images = useMemo(() => buildImageList(detail), [detail]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (open) {
      const max = Math.max(0, images.length - 1);
      setIndex(Math.min(Math.max(0, initialImageIndex), max));
    } else {
      setIndex(0);
    }
  }, [open, initialImageIndex, images.length]);

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);
  const goNext = useCallback(() => {
    setIndex((i) => Math.min(images.length - 1, i + 1));
  }, [images.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, goPrev, goNext]);

  if (!open || !detail) return null;

  const safeIndex = Math.min(Math.max(0, index), images.length - 1);
  const src = images[safeIndex];
  const hasOngoingIssues = (detail.issues || []).some((issue) => issue.status === 'Ongoing');
  const statusText = hasOngoingIssues ? 'Needs Service' : 'Healthy';
  const statusVariant = hasOngoingIssues ? 'yellow' : 'green';

  return (
    <Modal show={open} onHide={onClose} centered size="lg" fullscreen="sm-down" aria-labelledby="equipment-info-modal-title" className="manager-equipment-gallery">
      <Modal.Body style={{ padding: 0 }}>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 2,
            border: 'none',
            background: '#f3f4f6',
            borderRadius: 8,
            width: 44,
            height: 44,
            fontSize: 22,
            lineHeight: 1,
            cursor: 'pointer',
            color: '#374151',
          }}
        >
          ×
        </button>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
          }}
        >
          {/* Carousel */}
          <div
            style={{
              background: '#f3f4f6',
              borderRadius: '12px 12px 0 0',
              padding: 16,
              minHeight: 280,
            }}
          >
            <div className="manager-equipment-gallery-media" style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
              <Button variant="outline-secondary" aria-label="Previous equipment photo" onClick={goPrev} disabled={safeIndex === 0}>
                ‹
              </Button>
              <div
                style={{
                  flex: 1,
                  minHeight: 260,
                  maxHeight: '50vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {src && (
                  isVideoUrl(src) ? (
                    <video
                      key={safeIndex}
                      src={src}
                      controls
                      playsInline
                      style={{
                        maxWidth: '100%',
                        maxHeight: '50vh',
                        borderRadius: 8,
                      }}
                    />
                  ) : (
                    <img
                      src={src}
                      alt={detail.name || 'Equipment'}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '50vh',
                        objectFit: 'contain',
                        borderRadius: 8,
                      }}
                      onError={(e) => {
                        e.target.src = PLACEHOLDER;
                      }}
                    />
                  )
                )}
              </div>
              <Button variant="outline-secondary" aria-label="Next equipment photo" onClick={goNext} disabled={safeIndex >= images.length - 1}>
                ›
              </Button>
            </div>
            {images.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 12 }}>
                {images.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIndex(i)}
                    aria-label={`Go to image ${i + 1}`}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      background: i === safeIndex ? '#374151' : '#d1d5db',
                      transition: 'background 0.15s',
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div style={{ padding: '20px 24px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <h4 id="equipment-info-modal-title" style={{ margin: 0, fontSize: '1.25rem' }}>
                {detail.name}
              </h4>
              <Chip variant={statusVariant}>{statusText}</Chip>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(100px, auto) 1fr',
                rowGap: 10,
                columnGap: 20,
                fontSize: 14,
                marginBottom: 16,
              }}
            >
              <div style={{ color: '#1f2937', fontWeight: 600 }}>Location</div>
              <div style={{ color: '#6b7280' }}>{detail.location ?? '—'}</div>
              <div style={{ color: '#1f2937', fontWeight: 600 }}>Model</div>
              <div style={{ color: '#6b7280' }}>
                {[detail.manufacturer, detail.model].filter(Boolean).join(' ') || '—'}
              </div>
              <div style={{ color: '#1f2937', fontWeight: 600 }}>Date</div>
              <div style={{ color: '#6b7280' }}>{detail.year ?? '—'}</div>
              <div style={{ color: '#1f2937', fontWeight: 600 }}>Quantity</div>
              <div style={{ color: '#6b7280' }}>{detail.quantity ?? '—'}</div>
            </div>

            <div>
              <div style={{ color: '#1f2937', fontWeight: 600, marginBottom: 8 }}>Details</div>
              <div
                style={{
                  color: '#4b5563',
                  fontSize: 14,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {detail.details?.trim() ? detail.details : '—'}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export { buildImageList, PLACEHOLDER };
